import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { ColumnInfo } from '../api/types'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import type { TableInfo, PagedRowsResponse, FkEntryRequest } from '../api/types'
import { Pagination } from '../components/Pagination'
import { ConfirmModal } from '../components/ConfirmModal'
import { DeleteConfirmModal } from '../components/DeleteConfirmModal'
import { CreateEditForm } from '../components/CreateEditForm'
import { TimelineView } from '../components/TimelineView'
import { FileUpload } from '../components/FileUpload'
import { VersionHistory } from '../components/VersionHistory'
import { PendingChangeIndicator } from '../components/PendingChangeIndicator'
import { PendingChangeReview } from '../components/PendingChangeReview'
import { usePendingChanges } from '../hooks/usePendingChanges'
import { getFieldLabel, VIEW_COLUMNS } from '../config/procedureTypeFields'
import { formatNotesForDisplay, formatStructuredDataForDisplay } from '../utils/formatters'

// Mapping from view display columns to their source table info
// Views use display names (e.g., 'subject') instead of FK IDs (e.g., 'subject_id')
// This allows FK linking to work in views by mapping the display column to its source table
const VIEW_COLUMN_TO_SOURCE: Record<string, { referencedTable: string; referencedColumn: string }> = {
  'subject': { referencedTable: 'subjects', referencedColumn: 'display_name' },
  'banking_entity': { referencedTable: 'organizations', referencedColumn: 'name' },
  'performing_entity': { referencedTable: 'organizations', referencedColumn: 'name' },
  'location_performed': { referencedTable: 'organizations', referencedColumn: 'name' },
  'location_destination': { referencedTable: 'organizations', referencedColumn: 'name' },
  'transfer_origin': { referencedTable: 'organizations', referencedColumn: 'name' },
  'transfer_destination': { referencedTable: 'organizations', referencedColumn: 'name' },
  'source_sample_label': { referencedTable: 'samples', referencedColumn: 'title' },
  'blocked_by': { referencedTable: 'samples', referencedColumn: 'title' },
}

type ModalState =
  | { type: 'none' }
  | { type: 'create'; prefillDate?: Date }
  | { type: 'edit'; row: Record<string, unknown> }
  | { type: 'confirmCreate'; data: Record<string, unknown>; fkEntries?: FkEntryRequest[] }
  | { type: 'confirmEdit'; pk: string; data: Record<string, unknown>; fkEntries?: FkEntryRequest[] }
  | { type: 'confirmDelete'; pk: string }
  | { type: 'expandCell'; columnName: string; value: string }
  | { type: 'uploadFile' }
  | { type: 'versionHistory'; sampleId: string; sampleTitle?: string }

export function TableBrowser() {
  const { tableName } = useParams<{ tableName: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { apiClient, isAdmin } = useAuth()

  const [tableInfo, setTableInfo] = useState<TableInfo | null>(null)
  const [editTableInfo, setEditTableInfo] = useState<TableInfo | null>(null) // Base table schema for editing from views
  const [data, setData] = useState<PagedRowsResponse | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [modal, setModal] = useState<ModalState>({ type: 'none' })
  const [actionLoading, setActionLoading] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'timeline'>('table')

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')

  // Filtering state - initialize from URL params
  const [filterColumn, setFilterColumn] = useState<string>(() => searchParams.get('filterColumn') || '')
  const [filterValue, setFilterValue] = useState<string>(() => searchParams.get('filterValue') || '')
  const [filterInputValue, setFilterInputValue] = useState<string>(() => searchParams.get('filterValue') || '')
  const [filterColumnValues, setFilterColumnValues] = useState<string[]>([])
  const [filterValuesLoading, setFilterValuesLoading] = useState(false)

  // File upload state (for sample_files table)
  const [uploadSampleId, setUploadSampleId] = useState<string>('')
  const [availableSamples, setAvailableSamples] = useState<{ id: string; label: string }[]>([])
  const [samplesLoading, setSamplesLoading] = useState(false)

  // FK lookup data: maps referencedTable -> { id -> displayName }
  const [fkLookups, setFkLookups] = useState<Record<string, Record<string, string>>>({})

  // Debounce ref for data fetching
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Pending changes state
  const [expandedPendingRow, setExpandedPendingRow] = useState<string | null>(null)

  // Drag-to-scroll state for table (horizontal only)
  const [isTableDragging, setIsTableDragging] = useState(false)
  const [tableDragStart, setTableDragStart] = useState({ x: 0, scrollLeft: 0 })
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const tableVelocityRef = useRef(0)
  const tableLastMoveRef = useRef({ x: 0, time: 0 })
  const tableMomentumRef = useRef<number | null>(null)

  // Handle escape key to close modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modal.type !== 'none') {
        setModal({ type: 'none' })
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [modal.type])

  // Sync filter state when URL params change (e.g., navigating via FK link)
  useEffect(() => {
    const urlFilterColumn = searchParams.get('filterColumn') || ''
    const urlFilterValue = searchParams.get('filterValue') || ''
    setFilterColumn(urlFilterColumn)
    setFilterValue(urlFilterValue)
    setFilterInputValue(urlFilterValue)
    setPage(1)
  }, [tableName, searchParams])

  // Helper to fetch FK lookup data for a given table's foreign keys
  const fetchFkLookups = useCallback(async (info: TableInfo): Promise<Record<string, Record<string, string>>> => {
    if (!apiClient || info.foreignKeys.length === 0) {
      return {}
    }

    const referencedTables = [...new Set(info.foreignKeys.map(fk => fk.referencedTable))]
    const lookups: Record<string, Record<string, string>> = {}

    await Promise.all(
      referencedTables.map(async (refTable) => {
        try {
          const response = await apiClient.getRows(refTable, 1, 1000)
          const idToName: Record<string, string> = {}

          // Determine the display column and ID column based on table
          const displayCol = refTable === 'subjects' ? 'display_name' : (refTable === 'samples' ? 'title' : 'name')
          const fkInfo = info.foreignKeys.find(fk => fk.referencedTable === refTable)
          const idCol = fkInfo?.referencedColumn || 'id'

          for (const row of response.rows) {
            const id = row[idCol]
            const displayValue = row[displayCol]
            if (id !== null && id !== undefined) {
              idToName[String(id)] = displayValue ? String(displayValue) : String(id)
            }
          }

          lookups[refTable] = idToName
        } catch (err) {
          console.warn(`Failed to fetch lookup data for ${refTable}:`, err)
        }
      })
    )

    return lookups
  }, [apiClient])

  const loadData = useCallback(async () => {
    if (!apiClient || !tableName) return

    try {
      setLoading(true)
      setError(null)

      // Check if this is the master events timeline view
      const isTimeline = tableName === 'vw_master_events_timeline'

      // Use larger page size for timeline view (fetch all rows)
      const effectivePageSize = isTimeline ? 1000 : pageSize

      // Build options for sorting and filtering
      const options: {
        sortColumn?: string
        sortDirection?: 'asc' | 'desc'
        filterColumn?: string
        filterValue?: string
      } = {}

      if (sortColumn) {
        options.sortColumn = sortColumn
        options.sortDirection = sortDirection
      }

      if (filterColumn && filterValue) {
        options.filterColumn = filterColumn
        options.filterValue = filterValue
      }

      // First fetch table info to know the FK structure
      const info = await apiClient.getTableInfo(tableName)

      // Then fetch rows and FK lookups in parallel so lookups are ready when data renders
      const [rows, lookups] = await Promise.all([
        apiClient.getRows(tableName, isTimeline ? 1 : page, effectivePageSize, options),
        fetchFkLookups(info),
      ])

      // Set all state together to avoid flash
      setTableInfo(info)
      setFkLookups(lookups)
      setData(rows)

      // Auto-switch to timeline mode for master events timeline
      if (isTimeline && viewMode === 'table') {
        setViewMode('timeline')
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to load data')
      }
    } finally {
      setLoading(false)
    }
  }, [apiClient, tableName, page, pageSize, viewMode, sortColumn, sortDirection, filterColumn, filterValue, fetchFkLookups])

  // Debounced data loading to prevent rapid API calls
  useEffect(() => {
    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }

    // Set a new timeout for the data load (300ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      loadData()
    }, 300)

    // Cleanup on unmount or when dependencies change
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [loadData])

  // Reload data when pending changes are accepted/rejected
  useEffect(() => {
    const handlePendingChangesUpdated = () => {
      loadData()
    }
    window.addEventListener('pendingChangesUpdated', handlePendingChangesUpdated)
    return () => window.removeEventListener('pendingChangesUpdated', handlePendingChangesUpdated)
  }, [loadData])

  // Fetch base table schema for editing from views
  useEffect(() => {
    if (!apiClient || !tableName) return

    const isView = tableName.startsWith('vw_')
    if (isView) {
      // Fetch the samples table schema for editing
      apiClient.getTableInfo('samples').then(setEditTableInfo).catch(() => {
        // If we can't get the base table schema, fall back to view schema
        setEditTableInfo(null)
      })
    } else {
      // For base tables, use the same schema
      setEditTableInfo(null)
    }
  }, [apiClient, tableName])

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize)
    setPage(1)
  }

  const handleSort = (columnName: string) => {
    if (sortColumn === columnName) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to descending
      setSortColumn(columnName)
      setSortDirection('desc')
    }
    setPage(1) // Reset to first page on sort change
  }

  const handleApplyFilter = () => {
    setFilterValue(filterInputValue)
    setPage(1) // Reset to first page on filter change
    // Update URL params
    if (filterColumn && filterInputValue) {
      setSearchParams({ filterColumn, filterValue: filterInputValue })
    }
  }

  const handleClearFilter = () => {
    setFilterColumn('')
    setFilterValue('')
    setFilterInputValue('')
    setPage(1)
    // Clear URL params
    setSearchParams({})
  }

  // Helper to get FK info for a column (works for both base tables and views)
  const getFkInfo = useCallback((columnName: string): { referencedTable: string; referencedColumn: string } | null => {
    // First check actual FK metadata from table schema
    if (tableInfo) {
      const fk = tableInfo.foreignKeys.find(fk => fk.columnName === columnName)
      if (fk) {
        return { referencedTable: fk.referencedTable, referencedColumn: fk.referencedColumn }
      }
    }
    // Fall back to view column mapping (for views that use display names instead of FK IDs)
    return VIEW_COLUMN_TO_SOURCE[columnName] || null
  }, [tableInfo])

  // Table drag-to-scroll with momentum (horizontal only)
  const applyTableMomentum = () => {
    const container = tableScrollRef.current
    if (!container) return

    const friction = 0.95
    const minVelocity = 0.5

    if (Math.abs(tableVelocityRef.current) > minVelocity) {
      container.scrollLeft += tableVelocityRef.current
      tableVelocityRef.current *= friction
      tableMomentumRef.current = requestAnimationFrame(applyTableMomentum)
    } else {
      tableVelocityRef.current = 0
      tableMomentumRef.current = null
    }
  }

  const handleTableMouseDown = (e: React.MouseEvent) => {
    const container = tableScrollRef.current
    if (!container) return

    if (tableMomentumRef.current) {
      cancelAnimationFrame(tableMomentumRef.current)
      tableMomentumRef.current = null
    }

    setIsTableDragging(true)
    setTableDragStart({
      x: e.clientX,
      scrollLeft: container.scrollLeft,
    })
    tableLastMoveRef.current = { x: e.clientX, time: Date.now() }
    tableVelocityRef.current = 0
  }

  const handleTableMouseMove = (e: React.MouseEvent) => {
    if (!isTableDragging) return
    const container = tableScrollRef.current
    if (!container) return
    e.preventDefault()

    const now = Date.now()
    const dx = e.clientX - tableDragStart.x
    container.scrollLeft = tableDragStart.scrollLeft - dx

    const timeDelta = now - tableLastMoveRef.current.time
    if (timeDelta > 0) {
      tableVelocityRef.current = (tableLastMoveRef.current.x - e.clientX) / timeDelta * 16
    }
    tableLastMoveRef.current = { x: e.clientX, time: now }
  }

  const handleTableMouseUp = () => {
    setIsTableDragging(false)
    if (Math.abs(tableVelocityRef.current) > 0.5) {
      tableMomentumRef.current = requestAnimationFrame(applyTableMomentum)
    }
  }

  const handleTableMouseLeave = () => {
    if (isTableDragging) {
      setIsTableDragging(false)
      if (Math.abs(tableVelocityRef.current) > 0.5) {
        tableMomentumRef.current = requestAnimationFrame(applyTableMomentum)
      }
    }
  }

  // Get the target table for write operations (base table for views)
  const getWriteTargetTable = () => {
    return tableName?.startsWith('vw_') ? 'samples' : tableName
  }

  const handleCreate = async () => {
    if (modal.type !== 'confirmCreate' || !apiClient || !tableName) return

    const writeTable = getWriteTargetTable()
    if (!writeTable) return

    try {
      setActionLoading(true)
      // Use transactional API when there are FK entries to create
      if (modal.fkEntries && modal.fkEntries.length > 0) {
        await apiClient.createRowWithFk(writeTable, modal.data, modal.fkEntries)
      } else {
        await apiClient.createRow(writeTable, modal.data)
      }
      setModal({ type: 'none' })
      await loadData()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to create row')
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleUpdate = async () => {
    if (modal.type !== 'confirmEdit' || !apiClient || !tableName) return

    const writeTable = getWriteTargetTable()
    if (!writeTable) return

    try {
      setActionLoading(true)
      // Use transactional endpoint if FK entries need to be created
      if (modal.fkEntries && modal.fkEntries.length > 0) {
        await apiClient.updateRowWithFk(writeTable, modal.pk, modal.data, modal.fkEntries)
      } else {
        await apiClient.updateRow(writeTable, modal.pk, modal.data)
      }
      setModal({ type: 'none' })
      await loadData()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to update row')
      }
    } finally {
      setActionLoading(false)
    }
  }

  const handleDelete = async () => {
    if (modal.type !== 'confirmDelete' || !apiClient || !tableName) return

    const writeTable = getWriteTargetTable()
    if (!writeTable) return

    try {
      setActionLoading(true)
      await apiClient.deleteRow(writeTable, modal.pk)
      setModal({ type: 'none' })
      await loadData()
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message)
      } else {
        setError('Failed to delete row')
      }
    } finally {
      setActionLoading(false)
    }
  }

  const getPrimaryKeyValue = (row: Record<string, unknown>): string | null => {
    // For views, fall back to sample_id since views don't have a formal primary key
    const pkColumn = tableInfo?.primaryKeyColumn || (tableName?.startsWith('vw_') ? 'sample_id' : null)
    if (!pkColumn) {
      console.warn('[getPrimaryKeyValue] No primary key column defined for table:', tableName)
      return null
    }
    // Validate that the column actually exists in the row
    if (!(pkColumn in row)) {
      console.warn(`[getPrimaryKeyValue] Primary key column "${pkColumn}" not found in row for table: ${tableName}`)
      return null
    }
    const value = row[pkColumn]
    return value === null || value === undefined ? null : String(value)
  }

  const formatCellValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return ''
    }
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }
    return String(value)
  }

  // These must be before any conditional returns (Rules of Hooks)
  const isTimelineView = tableName === 'vw_master_events_timeline'

  // Compute visible columns - hide UUID primary keys in views and filter by view-specific columns
  const visibleColumns = useMemo((): ColumnInfo[] => {
    if (!tableInfo) return []
    const isView = tableName?.startsWith('vw_')

    // Get view-specific columns if defined
    const viewColumns = tableName ? VIEW_COLUMNS[tableName] : null

    return tableInfo.columns.filter(col => {
      // Hide UUID primary keys in views (data is still there for operations)
      if (isView && col.isPrimaryKey && col.dataType === 'uuid') {
        return false
      }
      // If view has specific columns defined, only show those
      if (viewColumns && !viewColumns.includes(col.columnName)) {
        return false
      }
      return true
    })
  }, [tableInfo, tableName])

  // Fetch distinct values for the selected filter column from the API
  useEffect(() => {
    if (!apiClient || !tableName || !filterColumn) {
      setFilterColumnValues([])
      return
    }

    const fetchDistinctValues = async () => {
      setFilterValuesLoading(true)
      try {
        const response = await apiClient.getDistinctColumnValues(tableName, filterColumn)
        setFilterColumnValues(response.values)
      } catch (err) {
        console.error('Failed to fetch distinct column values:', err)
        // Fallback to current page values if API call fails
        if (data?.rows) {
          const values = new Set<string>()
          for (const row of data.rows) {
            const val = row[filterColumn]
            if (val !== null && val !== undefined && val !== '') {
              values.add(String(val))
            }
          }
          setFilterColumnValues(Array.from(values).sort((a, b) => a.localeCompare(b)))
        }
      } finally {
        setFilterValuesLoading(false)
      }
    }

    fetchDistinctValues()
  }, [apiClient, tableName, filterColumn, data?.rows])

  // Table info to use for create/edit forms (base table for views, current table for base tables)
  const formTableInfo = editTableInfo || tableInfo

  // Target table name for create/update operations (base table for views)
  const targetTableName = tableName?.startsWith('vw_') ? 'samples' : tableName

  // Check if this is the sample_files table (for file upload feature)
  const isSampleFilesTable = tableName === 'sample_files'

  // Extract sample IDs for pending changes check
  const sampleIds = useMemo(() => {
    if (!data?.rows) return []
    // Only check for samples table and views that have sample_id
    const isSamplesRelated = tableName === 'samples' || tableName?.startsWith('vw_')
    if (!isSamplesRelated) return []
    return data.rows
      .map(row => String(row.sample_id || ''))
      .filter(id => id !== '')
  }, [data?.rows, tableName])

  // Fetch pending changes for visible samples
  const { pendingBySampleId } = usePendingChanges(apiClient, sampleIds)

  // Load available samples when upload modal is opened
  useEffect(() => {
    if (modal.type !== 'uploadFile' || !apiClient) return

    const loadSamples = async () => {
      setSamplesLoading(true)
      try {
        // Fetch samples with sample_id and a display column
        const response = await apiClient.getRows('samples', 1, 500, {
          sortColumn: 'created_at',
          sortDirection: 'desc',
        })
        const samples = response.rows.map((row) => ({
          id: String(row.sample_id),
          label: row.title
            ? `${row.title}${row.sample_type ? ` (${row.sample_type})` : ''}`
            : String(row.sample_id).slice(0, 8),
        }))
        setAvailableSamples(samples)
      } catch (err) {
        console.error('Failed to load samples:', err)
        setAvailableSamples([])
      } finally {
        setSamplesLoading(false)
      }
    }

    loadSamples()
  }, [modal.type, apiClient])

  if (loading && !data) {
    return <div className="loading">Loading table data...</div>
  }

  if (error && !data) {
    return (
      <div className="container">
        <div className="error">{error}</div>
        <Link to="/schema" style={{ display: 'block', marginTop: 'var(--spacing-md)' }}>
          Back to Schema Explorer
        </Link>
      </div>
    )
  }

  if (!tableInfo || !data) {
    return null
  }

  // Human-readable name mapping for views
  const getDisplayName = (name: string): string => {
    const viewNames: Record<string, string> = {
      'vw_master_events_timeline': 'Master Events Timeline',
      'vw_sample_status': 'Sample Status',
      'vw_outstanding_transfers': 'Outstanding Transfers',
      'vw_sampling_patient': 'Sampling Patient',
    }
    return viewNames[name] || name
  }

  return (
    <div className="container">
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 'var(--spacing-lg)',
      }}>
        <div>
          <h2 style={{ margin: 0 }}>
            {getDisplayName(tableName || '')}
          </h2>
          {tableName?.startsWith('vw_') && (
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-mono)' }}>
              {tableName}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
          {isAdmin && isSampleFilesTable && (
            <button
              className="btn btn-primary"
              onClick={() => {
                setUploadSampleId('')
                setModal({ type: 'uploadFile' })
              }}
            >
              Upload File
            </button>
          )}
          {isAdmin && !isTimelineView && !isSampleFilesTable && (
            <button
              className="btn btn-primary"
              onClick={() => setModal({ type: 'create' })}
            >
              + Create Row
            </button>
          )}
          {isAdmin && isTimelineView && (
            <button
              className="btn btn-primary"
              onClick={() => setModal({ type: 'create' })}
            >
              + Create Event
            </button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="error" style={{ marginBottom: 'var(--spacing-md)' }}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{
              marginLeft: 'var(--spacing-md)',
              background: 'none',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div className="card" style={{ padding: 'var(--spacing-lg)' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}>
            <span>{data.totalCount} events</span>
          </div>
          <TimelineView
            rows={data.rows}
            columns={tableInfo.columns}
            onEditClick={isAdmin ? (row) => setModal({ type: 'edit', row }) : undefined}
            onDeleteClick={isAdmin ? (row) => {
              const pk = getPrimaryKeyValue(row)
              if (pk) setModal({ type: 'confirmDelete', pk })
            } : undefined}
            onCreateClick={isAdmin ? (prefillDate) => setModal({ type: 'create', prefillDate }) : undefined}
            onHistoryClick={(row) => {
              const sampleId = String(row.sample_id || getPrimaryKeyValue(row))
              const sampleTitle = row.title ? String(row.title) : undefined
              setModal({ type: 'versionHistory', sampleId, sampleTitle })
            }}
            pendingChanges={pendingBySampleId}
          />
        </div>
      )}

      {/* Data table */}
      {viewMode === 'table' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {/* Filter controls */}
          <div style={{
            padding: 'var(--spacing-md)',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            gap: 'var(--spacing-sm)',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            <select
              value={filterColumn}
              onChange={(e) => setFilterColumn(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">Filter by column...</option>
              {visibleColumns.map((col) => (
                <option key={col.columnName} value={col.columnName}>
                  {getFieldLabel(col.columnName)}
                </option>
              ))}
            </select>
            <select
              value={filterInputValue}
              onChange={(e) => setFilterInputValue(e.target.value)}
              style={{ minWidth: '200px' }}
              disabled={!filterColumn || filterValuesLoading}
            >
              <option value="">
                {filterValuesLoading ? 'Loading values...' : 'Select value...'}
              </option>
              {filterColumnValues.map((val) => (
                <option key={val} value={val}>
                  {val}
                </option>
              ))}
            </select>
            <button
              className="btn btn-secondary"
              onClick={handleApplyFilter}
              disabled={!filterColumn || !filterInputValue}
            >
              Apply
            </button>
            {filterValue && (
              <button
                className="btn btn-secondary"
                onClick={handleClearFilter}
              >
                Clear
              </button>
            )}
            {filterValue && (
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                Filtering {getFieldLabel(filterColumn)} contains "{filterValue}"
              </span>
            )}
          </div>

          <div
            ref={tableScrollRef}
            className={`table-scroll-container ${isTableDragging ? 'dragging' : ''}`}
            style={{ overflow: 'auto', maxHeight: 'calc(100vh - 280px)' }}
            onMouseDown={handleTableMouseDown}
            onMouseMove={handleTableMouseMove}
            onMouseUp={handleTableMouseUp}
            onMouseLeave={handleTableMouseLeave}
          >
            <table>
            <thead>
              <tr>
                {isAdmin && <th style={{ width: '100px' }}>Actions</th>}
                {visibleColumns.map((col) => (
                  <th
                    key={col.columnName}
                    onClick={() => handleSort(col.columnName)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-xs)' }}>
                      {getFieldLabel(col.columnName)}
                      {sortColumn === col.columnName && (
                        <span style={{ fontSize: 'var(--font-size-xs)' }}>
                          {sortDirection === 'asc' ? '▲' : '▼'}
                        </span>
                      )}
                      {col.isPrimaryKey && (
                        <span className="badge badge-primary" style={{ marginLeft: 'var(--spacing-xs)' }}>
                          PK
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumns.length + (isAdmin ? 1 : 0)}
                    style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                data.rows.map((row, idx) => {
                  const pk = getPrimaryKeyValue(row)
                  const sampleId = String(row.sample_id || pk || '')
                  const pendingInfo = sampleId ? pendingBySampleId[sampleId] : undefined
                  // Check both batch result AND direct vendor_proposed column
                  const hasVendorProposed = !!row.vendor_proposed && typeof row.vendor_proposed === 'object' && Object.keys(row.vendor_proposed as object).length > 0
                  const hasPending = !!pendingInfo || hasVendorProposed
                  const isExpandedPending = expandedPendingRow === sampleId
                  return (
                    <React.Fragment key={pk || idx}>
                    <tr className={hasPending ? 'table-row-has-pending' : ''}>
                      {isAdmin && (
                        <td>
                          <div style={{ display: 'flex', gap: 'var(--spacing-xs)', alignItems: 'center' }}>
                            {hasPending && pendingInfo && (
                              <PendingChangeIndicator
                                pendingInfo={pendingInfo}
                                size="small"
                                showFieldCount={true}
                                onClick={() => setExpandedPendingRow(isExpandedPending ? null : sampleId)}
                              />
                            )}
                            {/* Fallback indicator when vendor_proposed exists but no pendingInfo yet */}
                            {hasPending && !pendingInfo && hasVendorProposed && (
                              <span
                                className="pending-dot"
                                title={`Pending changes from ${row.vendor_proposed_source || 'Vendor'}`}
                                onClick={() => setExpandedPendingRow(isExpandedPending ? null : sampleId)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => setModal({ type: 'edit', row })}
                              disabled={!pk}
                              title={!pk ? 'Cannot edit: Primary key unavailable for this row' : 'Edit row'}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-danger"
                              style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                              onClick={() => pk && setModal({ type: 'confirmDelete', pk })}
                              disabled={!pk}
                              title={!pk ? 'Cannot delete: Primary key unavailable for this row' : 'Delete row'}
                            >
                              Delete
                            </button>
                            {(tableName === 'samples' || tableName?.startsWith('vw_')) && (
                              <button
                                className="btn btn-secondary"
                                style={{ padding: '2px 8px', fontSize: 'var(--font-size-xs)' }}
                                onClick={() => {
                                  const sId = String(row.sample_id || pk)
                                  const sampleTitle = row.title ? String(row.title) : undefined
                                  setModal({ type: 'versionHistory', sampleId: sId, sampleTitle })
                                }}
                                disabled={!pk && !row.sample_id}
                                title="View version history"
                              >
                                History
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                      {visibleColumns.map((col) => {
                        const cellValue = formatCellValue(row[col.columnName])
                        const fkInfo = getFkInfo(col.columnName)
                        const isClickableFk = fkInfo && row[col.columnName] !== null
                        // Look up human-readable display name for FK values
                        const displayValue: string = (isClickableFk && fkInfo)
                          ? (fkLookups[fkInfo.referencedTable]?.[cellValue] ?? cellValue)
                          : cellValue
                        const isTruncated = displayValue.length > 40
                        return (
                          <td
                            key={col.columnName}
                            style={{
                              maxWidth: '300px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: isTruncated && !isClickableFk ? 'pointer' : 'default',
                            }}
                            title={isClickableFk ? `Go to ${fkInfo.referencedTable}: ${displayValue}` : (isTruncated ? 'Click to expand' : displayValue)}
                            onClick={() => {
                              if (isTruncated && !isClickableFk) {
                                setModal({ type: 'expandCell', columnName: col.columnName, value: cellValue })
                              }
                            }}
                          >
                            {row[col.columnName] === null ? (
                              <span className="text-muted">NULL</span>
                            ) : isClickableFk ? (
                              <Link
                                to={`/tables/${fkInfo.referencedTable}?filterColumn=${fkInfo.referencedColumn}&filterValue=${encodeURIComponent(cellValue)}`}
                                className="fk-link"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {displayValue}
                              </Link>
                            ) : (
                              <span className={col.dataType === 'uuid' ? 'mono' : ''}>
                                {displayValue}
                              </span>
                            )}
                          </td>
                        )
                      })}
                    </tr>
                    {/* Expandable pending changes row - inline review for vendor_proposed */}
                    {isExpandedPending && !!row.vendor_proposed && Object.keys(row.vendor_proposed as object).length > 0 && (
                      <tr className="table-row-pending-expanded">
                        <td colSpan={visibleColumns.length + (isAdmin ? 1 : 0)}>
                          <div className="pending-row-details">
                            <PendingChangeReview
                              sampleId={sampleId}
                              vendorProposed={row.vendor_proposed as Record<string, unknown>}
                              vendorSource={row.vendor_proposed_source as string | null}
                              vendorProposedAt={row.vendor_proposed_at as string | null}
                              onReviewComplete={() => {
                                setExpandedPendingRow(null)
                                loadData()
                              }}
                              compact={true}
                              rowData={row}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Legacy expandable pending changes row (for backward compatibility) */}
                    {hasPending && isExpandedPending && pendingInfo && !row.vendor_proposed && (
                      <tr className="table-row-pending-expanded">
                        <td colSpan={visibleColumns.length + (isAdmin ? 1 : 0)}>
                          <div className="pending-row-details">
                            <PendingChangeReview
                              sampleId={sampleId}
                              vendorProposed={Object.fromEntries(
                                Object.entries(pendingInfo.changes).map(([field, change]) => [field, change.new])
                              )}
                              vendorSource={pendingInfo.sourceVendor ?? null}
                              vendorProposedAt={pendingInfo.createdAt}
                              onReviewComplete={() => {
                                setExpandedPendingRow(null)
                                loadData()
                              }}
                              compact={true}
                              rowData={row}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          page={data.page}
          totalPages={data.totalPages}
          totalCount={data.totalCount}
          pageSize={data.pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
      )}

      {/* Modals */}
      {modal.type === 'create' && formTableInfo && (
        <CreateEditForm
          tableInfo={formTableInfo}
          mode="create"
          prefillDate={modal.prefillDate}
          onSubmit={(formData, fkEntries) => setModal({ type: 'confirmCreate', data: formData, fkEntries })}
          onCancel={() => setModal({ type: 'none' })}
          apiClient={apiClient || undefined}
          viewName={tableName}
        />
      )}

      {modal.type === 'edit' && formTableInfo && (
        <CreateEditForm
          tableInfo={formTableInfo}
          existingData={modal.row}
          mode="edit"
          onSubmit={(formData, fkEntries) => {
            const pk = getPrimaryKeyValue(modal.row)
            if (pk) {
              setModal({ type: 'confirmEdit', pk, data: formData, fkEntries })
            }
          }}
          onCancel={() => setModal({ type: 'none' })}
          apiClient={apiClient || undefined}
          viewName={tableName}
        />
      )}

      {modal.type === 'confirmCreate' && (
        <ConfirmModal
          title="Confirm Create"
          confirmLabel="Create Row"
          onConfirm={handleCreate}
          onCancel={() => setModal({ type: 'create' })}
          isLoading={actionLoading}
        >
          <p>You are about to create a new row in <strong>{targetTableName}</strong>.</p>
          <div style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            padding: 'var(--spacing-md)',
            borderRadius: 'var(--radius-md)',
            marginTop: 'var(--spacing-md)',
            maxHeight: '200px',
            overflow: 'auto',
          }}>
            <pre style={{
              margin: 0,
              fontSize: 'var(--font-size-xs)',
              fontFamily: 'var(--font-family-mono)',
            }}>
              {JSON.stringify(modal.data, null, 2)}
            </pre>
          </div>
        </ConfirmModal>
      )}

      {modal.type === 'confirmEdit' && (
        <ConfirmModal
          title="Confirm Update"
          confirmLabel="Save Changes"
          onConfirm={handleUpdate}
          onCancel={() => setModal({ type: 'none' })}
          isLoading={actionLoading}
        >
          <p>
            You are about to update row with PK <strong className="mono">{modal.pk}</strong> in <strong>{targetTableName}</strong>.
          </p>
          <div style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            padding: 'var(--spacing-md)',
            borderRadius: 'var(--radius-md)',
            marginTop: 'var(--spacing-md)',
            maxHeight: '200px',
            overflow: 'auto',
          }}>
            <pre style={{
              margin: 0,
              fontSize: 'var(--font-size-xs)',
              fontFamily: 'var(--font-family-mono)',
            }}>
              {JSON.stringify(modal.data, null, 2)}
            </pre>
          </div>
        </ConfirmModal>
      )}

      {modal.type === 'confirmDelete' && (
        <DeleteConfirmModal
          tableName={targetTableName || ''}
          primaryKey={modal.pk}
          onConfirm={handleDelete}
          onCancel={() => setModal({ type: 'none' })}
          isLoading={actionLoading}
        />
      )}

      {modal.type === 'expandCell' && (
        <div className="modal-overlay" onClick={() => setModal({ type: 'none' })}>
          <div className="modal" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>{getFieldLabel(modal.columnName)}</h3>
            </div>
            <div style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              padding: 'var(--spacing-md)',
              borderRadius: 'var(--radius-md)',
              maxHeight: '60vh',
              overflow: 'auto',
              wordBreak: 'break-word',
              fontSize: 'var(--font-size-sm)',
              ...(modal.columnName !== 'outcome_notes' && modal.columnName !== 'structured_data'
                ? { whiteSpace: 'pre-wrap', fontFamily: 'var(--font-family-mono)' }
                : {}),
            }}>
              {modal.columnName === 'outcome_notes'
                ? formatNotesForDisplay(modal.value)
                : modal.columnName === 'structured_data'
                  ? formatStructuredDataForDisplay(modal.value)
                  : modal.value}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setModal({ type: 'none' })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {modal.type === 'uploadFile' && (
        <div className="modal-overlay" onClick={() => setModal({ type: 'none' })}>
          <div className="modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ margin: 0 }}>Upload File</h3>
            </div>
            <div style={{ marginBottom: 'var(--spacing-md)' }}>
              <label style={{ display: 'block', marginBottom: 'var(--spacing-xs)', fontWeight: 500 }}>
                Sample
              </label>
              <select
                value={uploadSampleId}
                onChange={(e) => setUploadSampleId(e.target.value)}
                style={{ width: '100%' }}
                disabled={samplesLoading}
              >
                <option value="">
                  {samplesLoading ? 'Loading samples...' : 'Select a sample...'}
                </option>
                {availableSamples.map((sample) => (
                  <option key={sample.id} value={sample.id}>
                    {sample.label}
                  </option>
                ))}
              </select>
            </div>
            {uploadSampleId && (
              <FileUpload
                sampleId={uploadSampleId}
                onUploadComplete={() => {
                  setModal({ type: 'none' })
                  loadData()
                }}
              />
            )}
            {!uploadSampleId && (
              <div style={{
                padding: 'var(--spacing-lg)',
                textAlign: 'center',
                color: 'var(--color-text-muted)',
                backgroundColor: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-md)',
              }}>
                Select a sample to upload files
              </div>
            )}
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setModal({ type: 'none' })}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {modal.type === 'versionHistory' && (
        <VersionHistory
          sampleId={modal.sampleId}
          sampleTitle={modal.sampleTitle}
          onClose={() => setModal({ type: 'none' })}
        />
      )}
    </div>
  )
}
